/**
 * MethodologyComparisonPanel — Three family columns with coverage caps
 *
 * Phase 4.5 — redesign-p4-analacct-45b
 * BI, Coverage-Levers, Property columns + Coverage-Caps panel below.
 * Source data from existing methodology API.
 */

import React, { useEffect, useState } from 'react';
import { Scale, AlertTriangle, CheckCircle2, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';

// Import UI primitives
import { DataTable, Column } from '@/components/ui/DataTable';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';

// Import methodology API
import { getMethodology } from '@/services/methodologyApi';
import type {
  MethodologyResponse,
  MethodologyResult,
  MethodologyPosture,
} from '@/types/methodology';

export interface MethodologyComparisonPanelProps {
  reportId: string;
  reportData: any;
}

type MethodologyFamily = 'bi' | 'coverage_levers' | 'property';

interface FamilyConfig {
  id: MethodologyFamily;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const FAMILIES: FamilyConfig[] = [
  {
    id: 'bi',
    label: 'BI Methodologies',
    icon: BookOpen,
    description: 'Business interruption loss calculation methods',
  },
  {
    id: 'coverage_levers',
    label: 'Coverage Levers',
    icon: Scale,
    description: 'Policy limits, deductibles, and adjustments',
  },
  {
    id: 'property',
    label: 'Property Loss',
    icon: AlertTriangle,
    description: 'First-party property valuation methods',
  },
];

const POSTURE_BADGE: Record<MethodologyPosture, { label: string; variant: any }> = {
  conservative: { label: 'Conservative', variant: 'neutral' },
  neutral: { label: 'Neutral', variant: 'done' },
  aggressive: { label: 'Aggressive', variant: 'active' },
  conditional: { label: 'Conditional', variant: 'attention' },
};

// Methodology row component
function MethodologyRow({
  method,
  expanded,
  onToggle,
}: {
  method: MethodologyResult;
  expanded: boolean;
  onToggle: () => void;
}) {
  const posture = POSTURE_BADGE[method.posture];

  return (
    <div className="border-b border-[var(--color-border-faint)] last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-3 px-4 text-left transition-colors hover:bg-[var(--color-bg-hover)]"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-[var(--color-text-dim)]" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[var(--color-text-dim)]" />
          )}
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              {method.label}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              {method.source_engine}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <FinancialValue value={method.value} />
          <StatusBadge text={posture.label} variant={posture.variant} size="sm" />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pl-11">
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
                  Method
                </p>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                  {method.label}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
                  Attested
                </p>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                  {method.attested ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
                  Posture
                </p>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                  {method.posture}
                </p>
              </div>
            </div>

            {method.authority && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
                  Authority
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span
                    className="rounded-md bg-[var(--color-bg-base)] px-2 py-1 text-xs text-[var(--color-text-muted)]"
                  >
                    {method.authority}
                  </span>
                </div>
              </div>
            )}

            {method.assumptions.length > 0 && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
                  Assumptions
                </p>
                <ul className="mt-2 list-inside list-disc text-xs text-[var(--color-text-muted)]">
                  {method.assumptions.map((limitation, idx) => (
                    <li key={idx}>{limitation}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function MethodologyComparisonPanel({
  reportId,
  reportData,
}: MethodologyComparisonPanelProps) {
  const [methodologyData, setMethodologyData] = useState<MethodologyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedMethods, setExpandedMethods] = useState<Set<string>>(new Set());
  const [activeFamily, setActiveFamily] = useState<MethodologyFamily>('bi');

  useEffect(() => {
    if (!reportId) return;

    const fetchMethodology = async () => {
      try {
        setLoading(true);
        const data = await getMethodology(reportId);
        setMethodologyData(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch methodology:', err);
        setError(err.message || 'Failed to load methodology data');
      } finally {
        setLoading(false);
      }
    };

    fetchMethodology();
  }, [reportId]);

  const toggleMethod = (methodId: string) => {
    setExpandedMethods((prev) => {
      const next = new Set(prev);
      if (next.has(methodId)) {
        next.delete(methodId);
      } else {
        next.add(methodId);
      }
      return next;
    });
  };

  const getMethodsForFamily = (family: MethodologyFamily): MethodologyResult[] => {
    if (!methodologyData?.methods) return [];

    switch (family) {
      case 'bi':
        return methodologyData.methods.filter(
          (m) =>
            !m.label.toLowerCase().includes('property') &&
            !m.label.toLowerCase().includes('cap') &&
            !m.label.toLowerCase().includes('deductible')
        );
      case 'coverage_levers':
        return methodologyData.methods.filter(
          (m) =>
            m.label.toLowerCase().includes('cap') ||
            m.label.toLowerCase().includes('deductible') ||
            m.label.toLowerCase().includes('limit')
        );
      case 'property':
        return methodologyData.methods.filter((m) =>
          m.label.toLowerCase().includes('property')
        );
      default:
        return [];
    }
  };

  const coverageCaps = methodologyData?.methods.filter(
    (m) =>
      m.label.toLowerCase().includes('cap') ||
      m.label.toLowerCase().includes('limit')
  ) || [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 animate-pulse rounded-lg bg-[var(--color-bg-surface)]" />
        <div className="grid gap-4 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-64 animate-pulse rounded-[13px] bg-[var(--color-bg-surface)]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to Load Methodology"
        description={error}
        icon={<AlertTriangle />}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Family Selector */}
      <div className="flex flex-wrap gap-2">
        {FAMILIES.map((family) => {
          const Icon = family.icon;
          const isActive = activeFamily === family.id;
          const methods = getMethodsForFamily(family.id);

          return (
            <button
              key={family.id}
              onClick={() => setActiveFamily(family.id)}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-4 py-3 text-left transition-all',
                isActive
                  ? 'border-[var(--color-brand)] bg-[rgba(255,102,0,0.12)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-elevated)]'
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  isActive
                    ? 'bg-[var(--color-brand)] text-white'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]'
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p
                  className={cn(
                    'font-medium',
                    isActive
                      ? 'text-[var(--color-brand)]'
                      : 'text-[var(--color-text-primary)]'
                  )}
                >
                  {family.label}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  {methods.length} methods
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Family Panel */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="border-b border-[var(--color-border)] px-6 py-4">
          <div className="flex items-center gap-3">
            {(() => {
              const Icon = FAMILIES.find((f) => f.id === activeFamily)?.icon || BookOpen;
              return <Icon className="h-5 w-5 text-[var(--color-brand)]" />;
            })()}
            <div>
              <h2
                className="text-lg font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                {FAMILIES.find((f) => f.id === activeFamily)?.label}
              </h2>
              <p className="text-sm text-[var(--color-text-muted)]">
                {FAMILIES.find((f) => f.id === activeFamily)?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-[var(--color-border-faint)]">
          {getMethodsForFamily(activeFamily).map((method) => (
            <MethodologyRow
              key={method.method_id}
              method={method}
              expanded={expandedMethods.has(method.method_id)}
              onToggle={() => toggleMethod(method.method_id)}
            />
          ))}

          {getMethodsForFamily(activeFamily).length === 0 && (
            <div className="p-8 text-center">
              <p className="text-sm text-[var(--color-text-muted)]">
                No methodologies available for this family.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Coverage Caps Panel */}
      {coverageCaps.length > 0 && (
        <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
          <h3
            className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
          >
            Coverage Caps & Limits
          </h3>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {coverageCaps.map((cap) => (
              <MetricCard
                key={cap.method_id}
                label={cap.label}
                value={<FinancialValue value={cap.value} />}
                subtitle={`Posture: ${cap.posture}`}
                badge={
                  cap.attested
                    ? { text: 'Attested', variant: 'done' }
                    : cap.contested
                      ? { text: 'Contested', variant: 'blocked' }
                      : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Comparison Summary */}
      {methodologyData?.methods && methodologyData.methods.length > 0 && (
        <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
          <h3
            className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
          >
            Methodology Summary
          </h3>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Methods"
              value={methodologyData.methods.length.toString()}
            />
            <MetricCard
              label="Conservative Floor"
              value={
                <FinancialValue
                  value={Math.min(
                    ...methodologyData.methods.map((r) => r.value || 0)
                  )}
                />
              }
              badge={{ text: 'Floor', variant: 'neutral' }}
            />
            <MetricCard
              label="Aggressive Ceiling"
              value={
                <FinancialValue
                  value={Math.max(
                    ...methodologyData.methods.map((r) => r.value || 0)
                  )}
                />
              }
              badge={{ text: 'Ceiling', variant: 'active' }}
            />
            <MetricCard
              label="Average"
              value={
                <FinancialValue
                  value={
                    methodologyData.methods.reduce(
                      (sum, r) => sum + (r.value || 0),
                      0
                    ) / methodologyData.methods.length
                  }
                />
              }
              badge={{ text: 'Mean', variant: 'done' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MethodologyComparisonPanel;
